import React, { useState, useEffect } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useOrgId } from '../../context/OrgContext';
import { useOrgData } from '../../hooks/useOrgData';

const TODAY = new Date().toISOString().substring(0, 10);

const fmt = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const BalanceSheet = () => {
  const orgId = useOrgId();
  const { accounts, funds } = useOrgData(orgId);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [asOf, setAsOf] = useState(TODAY);

  // A balance sheet is a snapshot, so this loads everything once and filters
  // to `date <= asOf` client-side rather than re-querying per date change.
  useEffect(() => {
    if (!orgId) return;
    setLoading(true);
    const unsub = onSnapshot(collection(db, 'orgs', orgId, 'transactions'), snap => {
      setTransactions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [orgId]);

  const activeAccounts = accounts.filter(a => a.active !== false);
  const activeFunds    = funds.filter(f => f.active !== false);
  const asOfTx         = transactions.filter(t => t.date && t.date <= asOf);

  // ── Assets: each bank account's balance as of the selected date ──────────────
  const acctBal = {};
  activeAccounts.forEach(a => {
    acctBal[a.name] = asOfTx
      .filter(t => t.account === a.name)
      .reduce((s, t) => s + (t.income || 0) - (t.expense || 0), 0);
  });

  // ── Liabilities: each fund's balance as of the selected date, incl. split lines ──
  const fundBal = {};
  activeFunds.forEach(f => { fundBal[f.name] = 0; });

  asOfTx.forEach(t => {
    if (t.split && t.lines?.length) {
      t.lines.forEach(line => {
        const fn = line.classification;
        if (!fn) return;
        if (!(fn in fundBal)) fundBal[fn] = 0;
        fundBal[fn] += t.income > 0 ? (line.amount || 0) : -(line.amount || 0);
      });
    } else {
      const fn = t.classification;
      if (!fn) return;
      if (!(fn in fundBal)) fundBal[fn] = 0;
      fundBal[fn] += (t.income || 0) - (t.expense || 0);
    }
  });

  const totalAssets       = activeAccounts.reduce((s, a) => s + (acctBal[a.name] || 0), 0);
  // This app doesn't track true liabilities (loans, payables) — the org has none,
  // so this is always $0. Kept as its own line so the equation still reads as
  // Assets = Liabilities + Fund Balances if that ever changes.
  const totalLiabilities  = 0;
  const totalFundBalances = activeFunds.reduce((s, f) => s + (fundBal[f.name] || 0), 0);
  const difference        = totalAssets - (totalLiabilities + totalFundBalances);
  const balanced          = Math.abs(difference) < 0.02;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Account Balances</h2>
          <p className="text-slate-500 text-sm mt-0.5">Assets, liabilities, and fund balances as of {asOf}</p>
        </div>
        <div className="flex gap-2 no-print">
          <input
            type="date" value={asOf} onChange={e => setAsOf(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white"
          />
          <button
            onClick={() => window.print()}
            className="border border-gray-300 px-3 py-1.5 rounded-lg text-sm hover:bg-gray-50 transition-colors"
          >
            Print
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-500">Loading…</div>
      ) : (
        <div className="max-w-2xl flex flex-col gap-6">

          {/* Assets, with Fund Balances broken out underneath */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="px-4 py-3 bg-slate-700 text-white">
              <h3 className="font-semibold text-sm">Assets</h3>
            </div>
            <table className="w-full text-sm">
              <tbody className="divide-y divide-gray-100">
                {activeAccounts.length === 0 ? (
                  <tr><td className="px-4 py-4 text-center text-gray-500 text-xs">No accounts set up.</td></tr>
                ) : activeAccounts.map(a => (
                  <tr key={a.name} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 font-medium">{a.name}</td>
                    <td className="px-4 py-2.5 text-right font-semibold">{fmt(acctBal[a.name])}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-100 font-bold border-t-2 border-slate-200">
                  <td className="px-4 py-2.5 text-sm">Total Assets</td>
                  <td className="px-4 py-2.5 text-right">{fmt(totalAssets)}</td>
                </tr>
              </tfoot>
            </table>

            <div className="px-4 pt-4 pb-1 bg-slate-50 border-t border-slate-200">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Fund Balances</h4>
              <p className="text-xs text-slate-400 mt-0.5">How total assets break down by fund</p>
            </div>
            <table className="w-full text-sm">
              <tbody className="divide-y divide-gray-100">
                {activeFunds.length === 0 ? (
                  <tr><td className="px-4 py-4 text-center text-gray-500 text-xs">No funds set up.</td></tr>
                ) : activeFunds.map(f => (
                  <tr key={f.name} className={`hover:bg-gray-50 ${fundBal[f.name] === 0 ? 'opacity-40' : ''}`}>
                    <td className="pl-8 pr-4 py-2 text-slate-600">{f.name}</td>
                    <td className="px-4 py-2 text-right">{fmt(fundBal[f.name])}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-100 font-bold border-t-2 border-slate-200">
                  <td className="px-4 py-2.5 text-sm">Total Fund Balances</td>
                  <td className="px-4 py-2.5 text-right">{fmt(totalFundBalances)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Reconciliation check */}
          {activeAccounts.length > 0 && activeFunds.length > 0 && (
            <div className={`rounded-xl px-4 py-3 flex items-center gap-3 ${
              balanced
                ? 'bg-green-50 border border-green-200 text-green-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
              <span className="text-xl">{balanced ? '✓' : '⚠'}</span>
              <div>
                <div className="font-semibold text-sm">
                  {balanced
                    ? `Balanced — Total Assets ${fmt(totalAssets)} match Total Fund Balances ${fmt(totalFundBalances)}`
                    : `Out of balance — Assets ${fmt(totalAssets)} vs Fund Balances ${fmt(totalFundBalances)}`}
                </div>
                {!balanced && (
                  <div className="text-xs mt-0.5">Difference: {fmt(Math.abs(difference))}</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BalanceSheet;
